import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from 'moment';
import 'moment-timezone';
import { ACTION_STATUS_ACTIVE, ACTION_STATUS_DISABLED_BY_FAILURE, ACTION_STATUS_WAITING_SUB_ACTIONS } from './constants';
import IgnoreProblemModal from './IgnoreProblemModal';
import config from '../../../../../config';
import $ from 'jquery';

export const StatusColor = {
  NEW: '#07d4f5',
  ACTIVE: '#17a2b8',
  DONE: '#00aa00',
  FAILED: '#ff4400',
  MIA: '',
  EXPIRED: 'black',
  WAITING_SUB_ACTION: '#2007ec',
  WAITING_EXECUTOR_ACTION: '#2007ec',
  REJECTED: '#888888',
  INCOMPLETE: '#1ed08e',
  POST_RESTARTING: '#e8a100',
  WAITING_RESTART: '#e8a100',
  DISABLED_BY_FAILURE: '#d63c04',
};

export const StatusCell = ({value, className, row, onShowProgress, onClickShowActiveActions, onClickShowFailedActions, onClickStatusAction, onRestartAction, user, onIgnoreProblem, onNavigateToRenderQueue}) => {
  const {showProgress, progress, problemLabel, problemText, problemEscalationPlan, actionID, actionType, actionTypeID, firstRecordedOn, ignoredBy, ignoredOn, ignoreReason, parameters} = row || {};
  const [showIgnoreModal, setShowIgnoreModal] = useState(false);
  const [renderQueueAssociation, setRenderQueueAssociation] = useState(null); // {queueID, jobID} or null
  const allowClickByProgress = !!onShowProgress && value === ACTION_STATUS_ACTIVE;
  const isDisabledByFailure = value === ACTION_STATUS_DISABLED_BY_FAILURE;
  const isWaitingSubActions = value === ACTION_STATUS_WAITING_SUB_ACTIONS;
  const showProgressBar  = !!showProgress && value !== 'FAILED' && value !== 'DONE';
  const isFailed = value === 'FAILED';
  const userEmail = user && user.email ? user.email.toLowerCase() : '';
  const hasProblem = !!problemLabel; // isAuthorizedUser && 
  const isAuthorizedUser = userEmail && (userEmail.includes('berkman') || userEmail.includes('morozov')); // true;
  const isMuted = !!ignoredBy; // Action is muted if ignoredBy is not null
  
  // Extract DESTINATION parameter value
  const getDestinationParameter = () => {
    if (!parameters || !Array.isArray(parameters)) return null;
    const destParam = parameters.find(p => p && (p.name === 'DESTINATION' || p.Name === 'DESTINATION'));
    if (!destParam) return null;
    return destParam.stringValue || destParam.StringValue || destParam.value || destParam.Value || null;
  };
  
  // Determine hot folder type and file name from DESTINATION path
  const getHotFolderInfo = (destinationPath) => {
    if (!destinationPath || typeof destinationPath !== 'string') {
      return { hotFolderType: null, fileName: null };
    }
    
    let hotFolderType = null;
    let fileName = null;
    
    // Determine hot folder type based on file extension
    if (destinationPath.toLowerCase().endsWith('.mp4')) {
      hotFolderType = 'PROXY';
    } else if (destinationPath.toLowerCase().endsWith('_autoqc.jpg')) {
      hotFolderType = 'EDGE_PASS';
    } else if (destinationPath.toLowerCase().endsWith('_hdr_report.pdf')) {
      hotFolderType = 'HDR REPORT';
    }
    
    // Extract file name (last segment after last backslash or forward slash)
    const lastSlashIndex = Math.max(destinationPath.lastIndexOf('\\'), destinationPath.lastIndexOf('/'));
    if (lastSlashIndex >= 0 && lastSlashIndex < destinationPath.length - 1) {
      fileName = destinationPath.substring(lastSlashIndex + 1);
      
      // Replace extension: .mp4, _autoqc.jpg, or _HDR_report.pdf with .mov
      fileName = fileName.replace(/([.]mp4|_autoqc[.]jpg|_HDR_report[.]pdf)$/i, '.mov');
    } else {
      fileName = destinationPath;
      // Replace extension if present
      fileName = fileName.replace(/([.]mp4|_autoqc[.]jpg|_HDR_report[.]pdf)$/i, '.mov');
    }
    
    return { hotFolderType, fileName };
  };
  
  // Replace placeholders in problemText
  const formatProblemText = (text) => {
    if (!text) return text;
    let formatted = text;
    
    // Replace %ACTION_ID%
    if (actionID) {
      formatted = formatted.replace(/%ACTION_ID%/g, String(actionID));
    }
    
    // Replace %ACTION_TYPE%
    if (actionType) {
      formatted = formatted.replace(/%ACTION_TYPE%/g, String(actionType));
    }
    
    // Replace %TIME_SINCE_FIRST_RECORDED% (and any trailing " min"/" mins")
    if (firstRecordedOn) {
      // firstRecordedOn is stored in DB as LOCAL (PDT) and sent as-is
      // However, if it has a "Z" suffix, it's being treated as UTC incorrectly
      // Remove the "Z" and parse as PDT timezone to get correct time difference
      let firstRecordedOnFixed = firstRecordedOn;
      if (typeof firstRecordedOnFixed === 'string' && firstRecordedOnFixed.endsWith('Z')) {
        // Remove the "Z" suffix - the time is already in PDT, not UTC
        firstRecordedOnFixed = firstRecordedOnFixed.replace(/Z$/, '');
      }
      // Parse as PDT timezone (treat the string as if it's already in PDT)
      const firstRecorded = moment.tz(firstRecordedOnFixed, 'America/Los_Angeles');
      const now = moment();

      const diffMinutesTotal = now.diff(firstRecorded, 'minutes');

      const days = Math.floor(diffMinutesTotal / (24 * 60));
      const hours = Math.floor((diffMinutesTotal % (24 * 60)) / 60);
      const minutes = diffMinutesTotal % 60;

      const plural = (n) => (n > 1 ? 's' : '');

      const parts = [];
      if (days > 0) parts.push(`${days} day${plural(days)}`);
      if (hours > 0) parts.push(`${hours} hour${plural(hours)}`);
      parts.push(`${minutes} min${plural(minutes)}`);

      formatted = formatted.replace(
        /%TIME_SINCE_FIRST_RECORDED%(?:\s+min(?:s)?)?/gi,
        parts.join(' ')
      );
    }
    
    // Replace %HOT_FOLDER_TYPE% and %FILE_NAME% using DESTINATION parameter
    const destinationPath = getDestinationParameter();
    if (destinationPath) {
      const { hotFolderType, fileName } = getHotFolderInfo(destinationPath);
      if (hotFolderType) {
        formatted = formatted.replace(/%HOT_FOLDER_TYPE%/g, hotFolderType);
      }
      if (fileName) {
        formatted = formatted.replace(/%FILE_NAME%/g, fileName);
      }
    }
    
    return formatted;
  };
  
  const formattedProblemText = formatProblemText(problemText);
  const formattedEscalation = formatProblemText(problemEscalationPlan);
  
  // Get hot folder type for the modal
  const destinationPath = getDestinationParameter();
  const { hotFolderType } = destinationPath ? getHotFolderInfo(destinationPath) : { hotFolderType: null };
  
  // Check if action is WAIT_FILE_EMERGENCE (type 96) or RENDER_PROXY_COPY (type 161)
  // Both action types can have render queue associations
  const isRenderQueueAction = actionType === 'WAIT_FILE_EMERGENCE' || actionType === 96 || actionTypeID === 96 ||
                              actionType === 'RENDER_PROXY_COPY' || actionType === 161 || actionTypeID === 161;
  
  // Check render queue association for render queue actions without problems
  // Only check if user is authorized
  useEffect(() => {
    if (isRenderQueueAction && actionID && !hasProblem && onNavigateToRenderQueue && isAuthorizedUser) {
      $.ajax({
        method: 'GET',
        url: `${config.apiUrl}/v1/file_system/mam/transkoder/render_queue/action_associations`,
        data: { actionID },
        dataType: 'json'
      })
      .done((data) => {
        if (data && data.queueID && data.jobID) {
          setRenderQueueAssociation({
            queueID: data.queueID,
            jobID: data.jobID
          });
        } else {
          setRenderQueueAssociation(null);
        }
      })
      .fail(() => {
        setRenderQueueAssociation(null);
      });
    } else {
      setRenderQueueAssociation(null);
    }
  }, [actionID, isRenderQueueAction, hasProblem, onNavigateToRenderQueue, isAuthorizedUser]);
  
  const handleProblemClick = () => {
    setShowIgnoreModal(true);
  };
  
  const handleIgnoreSuccess = () => {
    setShowIgnoreModal(false);
    if (onIgnoreProblem) {
      onIgnoreProblem(row);
    }
  };
  
  const handleRenderQueueClick = (e) => {
    e.stopPropagation();
    if (onNavigateToRenderQueue && renderQueueAssociation) {
      onNavigateToRenderQueue(renderQueueAssociation.queueID, renderQueueAssociation.jobID);
    }
  };

  let loadActionsBtn = null;
  let actionsTitle = null;
  if (isWaitingSubActions) {
    loadActionsBtn = <span className='span-clickable' onClick={() => onClickShowActiveActions(row)}>View active action(s)</span>;
    actionsTitle = 'Active Action(s)';
  }
  if (isDisabledByFailure) {
    loadActionsBtn = <span className='span-clickable' onClick={() => onClickShowFailedActions(row)}>View failed action(s)</span>;
    actionsTitle = 'Failed Action(s)';
  }

  let actionsList = null;
  if (row.actions) {
    if (row.actions.length > 0) {
      actionsList = row.actions.map(a => {
        return <div key={a.actionID}>{a.actionType} (<span className='span-clickable' onClick={() => onClickStatusAction(row, a)}>{a.actionID}</span>)</div>;
      });
    } else {
      actionsList = <div>No actions</div>;
    }
  }

  let showLoadActionsBtn = loadActionsBtn && (row.actions === undefined || (row.actions && row.actions.length === 0));

  return (
    <StyledCell>
      <div
        className='status'
        style={StatusColor[value] ? {backgroundColor: StatusColor[value]} : undefined}
      >
        {value}
      </div>
      {hasProblem && (
        <>
          <div 
            className='problem problem-clickable' 
            style={{
              backgroundColor: isMuted ? '#666666' : 'rgb(255, 0, 0)', 
              color: '#fff', 
              padding: '2px 4px', 
              borderRadius: '3px', 
              marginTop: '3px', 
              display: 'inline-block', 
              cursor: 'pointer'
            }}
            title={formattedProblemText && formattedEscalation ? `${formattedProblemText}\n\nEscalation: ${formattedEscalation}` : formattedProblemText || formattedEscalation}
            onClick={handleProblemClick}
          >
            {isMuted ? `MUTED ${problemLabel}` : problemLabel}
          </div>
          {showIgnoreModal && (
            <IgnoreProblemModal
              actionID={actionID}
              actionType={actionType}
              actionTypeID={actionTypeID}
              problemText={formattedProblemText}
              escalationPlan={formattedEscalation}
              ignoredBy={ignoredBy}
              ignoredOn={ignoredOn}
              ignoreReason={ignoreReason}
              isMuted={isMuted}
              hotFolderType={hotFolderType}
              onClose={() => setShowIgnoreModal(false)}
              onIgnore={handleIgnoreSuccess}
              onNavigateToRenderQueue={onNavigateToRenderQueue}
              user={user}
            />
          )}
        </>
      )}
      {!hasProblem && isRenderQueueAction && renderQueueAssociation && onNavigateToRenderQueue && isAuthorizedUser && (
        <div 
          style={{
            backgroundColor: '#28a745', 
            color: '#fff', 
            padding: '2px 4px', 
            borderRadius: '3px', 
            marginTop: '3px', 
            display: 'inline-block', 
            cursor: 'pointer'
          }}
          onClick={handleRenderQueueClick}
          title="Click to view this action in the render queue"
        >
          See in Render Queue
        </div>
      )}
      {isFailed && <div><span className='span-clickable' onClick={() => onRestartAction(row)}>Restart</span></div>}
      {showLoadActionsBtn && <div>{loadActionsBtn}</div>}
      {actionsList && <div><span>{actionsTitle}:</span>{actionsList}</div>}
      {showProgressBar &&
        <div
          className={`progress${allowClickByProgress ? '' : ' disabled'}`}
          onClick={allowClickByProgress ? () => onShowProgress(row) : undefined}
          title={`Progress: ${progress || 0}%`}
        >
          <div
            className="progress-bar"
            style={{background: StatusColor.DONE, width: `${progress || 0}%`}}
          >
            {progress >= 25 ? `${progress}%` : ''}
          </div>
        </div>
      }
    </StyledCell>
  );
};

const StyledCell = styled.td`
  .status {
    background-color: #343a40;
    border: 1px solid transparent;
    border-radius: 5px;
    color: #fff;
    display: inline-block;
    margin: 0 3px 3px 0;
    padding: 0 4px;
  }
  .status_clickable {
    cursor: pointer;
  }
  .progress {
    min-width: 50px;
    width: 100%;
    height: 20px;
    margin: 20px 0 0;
    overflow: hidden;
    background-color: #f5f5f5;
    border-radius: 4px;
    box-shadow: inset 0 1px 2px rgb(0 0 0 / 10%);
    cursor: pointer;
    .progress-bar {
      float: left;
      width: 0%;
      height: 100%;
      font-size: 12px;
      line-height: 20px;
      color: #fff;
      text-align: center;
      background-color: #337ab7;
      box-shadow: inset 0 -1px 0 rgb(0 0 0 / 15%);
      transition: width .6s ease;
    }
    &.disabled {
      cursor: default;
    }
  }
  .span-clickable {
    color: #007bff;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
  .problem-clickable {
    cursor: pointer;
    &:hover {
      opacity: 0.9;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    }
  }
`;

StatusCell.propTypes = {
  value: PropTypes.any,
  className: PropTypes.string,
  row: PropTypes.object,
  onShowProgress: PropTypes.func,
  onRestartAction: PropTypes.func,
  user: PropTypes.object,
  onIgnoreProblem: PropTypes.func,
};
